import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './components/LoginPage/LoginPage';
import AppLayout from './components/AppLayout/AppLayout';
import DashboardPage from './pages/DashboardPage';
import PlaceholderPage from './pages/PlaceholderPage';
import GolfCoursesPage from './pages/GolfCoursesPage';
import GolfCourseFormPage from './pages/GolfCourseFormPage';
import CalendarPage from './pages/CalendarPage';
import BookingsPage from './pages/BookingsPage';
import BookingFormPage from './pages/BookingFormPage';
import UsersPage from './pages/UsersPage';
import UserFormPage from './pages/UserFormPage';
import SettingsPage from './pages/SettingsPage';
import BlocksPage from './pages/BlocksPage';
import BlockFormPage from './pages/BlockFormPage';
import ReportsPage from './pages/ReportsPage';
import BookingsReportPage from './pages/BookingsReportPage';
import OccupancyReportPage from './pages/OccupancyReportPage';
import PlayersReportPage from './pages/PlayersReportPage';
import TopMembersReportPage from './pages/TopMembersReportPage';
import HeatmapReportPage from './pages/HeatmapReportPage';
import BookingsChartPage from './pages/BookingsChartPage';

// Auth guard: redirect to /login if no access_token in localStorage
function RequireAuth({ children }) {
  const token = localStorage.getItem('access_token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function App() {
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const handleAuthSuccess = (user) => {
    setCurrentUser(user);
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setCurrentUser(null);
  };

  const isAuthenticated = Boolean(localStorage.getItem('access_token'));

  return (
    <BrowserRouter>
      <Routes>
        {/* Root: redirect based on auth state */}
        <Route
          path="/"
          element={
            isAuthenticated
              ? <Navigate to="/dashboard" replace />
              : <Navigate to="/login" replace />
          }
        />

        {/* Login */}
        <Route
          path="/login"
          element={
            isAuthenticated
              ? <Navigate to="/dashboard" replace />
              : <LoginPage onAuthSuccess={handleAuthSuccess} />
          }
        />

        {/* Authenticated layout route — all protected pages share AppLayout */}
        <Route
          element={
            <RequireAuth>
              <AppLayout user={currentUser} onLogout={handleLogout} />
            </RequireAuth>
          }
        >
          <Route path="/dashboard" element={<DashboardPage user={currentUser} />} />
          <Route path="/calendar" element={<CalendarPage user={currentUser} />} />
          <Route path="/golf-courses" element={<GolfCoursesPage user={currentUser} />} />
          <Route path="/golf-courses/new" element={<GolfCourseFormPage user={currentUser} />} />
          <Route path="/golf-courses/:id" element={<GolfCourseFormPage user={currentUser} />} />
          <Route path="/tee-times" element={<PlaceholderPage title="Tee times" />} />
          <Route path="/bookings" element={<BookingsPage user={currentUser} />} />
          <Route path="/bookings/:id" element={<BookingFormPage user={currentUser} />} />
          <Route path="/blocks" element={<BlocksPage user={currentUser} />} />
          <Route path="/blocks/new" element={<BlockFormPage user={currentUser} />} />
          <Route path="/blocks/:id" element={<BlockFormPage user={currentUser} />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/reports/bookings" element={<BookingsReportPage user={currentUser} />} />
          <Route path="/reports/occupancy" element={<OccupancyReportPage user={currentUser} />} />
          <Route path="/reports/players" element={<PlayersReportPage user={currentUser} />} />
          <Route path="/reports/top-members" element={<TopMembersReportPage user={currentUser} />} />
          <Route path="/reports/heatmap" element={<HeatmapReportPage user={currentUser} />} />
          <Route path="/reports/bookings-chart" element={<BookingsChartPage user={currentUser} />} />
          <Route path="/users" element={<UsersPage user={currentUser} />} />
          <Route path="/users/new" element={<UserFormPage user={currentUser} />} />
          <Route path="/users/:id" element={<UserFormPage user={currentUser} />} />
          <Route path="/settings" element={<SettingsPage user={currentUser} />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
